import React, { forwardRef, useMemo, useState } from 'react';
import styles from './PasswordField.module.css';
import { Field, FieldProps } from '../_base/Field';
import type { TextFieldProps as AriaTextFieldProps } from 'react-aria-components';
import {
  TextField as AriaTextField,
  Input,
  Group,
} from 'react-aria-components';
import IconButton from '../IconButton';
import { cva } from 'class-variance-authority';
import Icon from '../Icon/Icon';

type PasswordStrengthStatus = 'valid' | 'neutral' | 'invalid';

const DEFAULT_SR_LABELS: Record<PasswordStrengthStatus, string> = {
  valid: 'Requirement met',
  neutral: 'Requirement',
  invalid: 'Requirement not met',
};

export type PasswordStrengthValidation = {
  message: string;
  validation: (value?: string) => boolean;
};

export interface PasswordFieldProps
  extends Omit<FieldProps<AriaTextFieldProps>, 'maxWidthByChars'> {
  /**
   * Whether the password input value revealed or not.
   */
  isVisible?: boolean;
  /**
   * The strength validations to be applied to the password input value.
   */
  strengthValidations?: PasswordStrengthValidation[];
  /**
   * Tooltip label to show password
   */
  tooltipLabelShowPassword?: string;
  /**
   * Tooltip label to hide password
   */
  tooltipLabelHidePassword?: string;
  /**
   * Label for the password requirements.
   */
  requirementsLabel?: string;
  /**
   * Placeholder for the password input.
   */
  placeholder?: string;
  /**
   * Screen reader text for each status, used for localization
   */
  screenReaderStrengthLabels?: Partial<Record<PasswordStrengthStatus, string>>;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  (
    {
      screenReaderStrengthLabels,
      tooltipLabelShowPassword = 'Show Password',
      tooltipLabelHidePassword = 'Hide Password',
      requirementsLabel = 'Password requirements',
      ...props
    },
    ref,
  ) => {
    // Internal state for uncontrolled component behavior
    const [internalVisible, setInternalVisible] = useState(
      props.isVisible || false,
    );
    const [internalValue, setInternalValue] = useState(props?.defaultValue);

    const value = props?.value ?? internalValue;
    const isVisibleState = props?.isVisible ?? internalVisible;

    const requirementsId = React.useId();

    const hasValidations = !!props.strengthValidations?.length;

    // Build aria-describedby to include requirements list if present
    const ariaDescribedBy =
      [
        hasValidations && requirementsId,
        props['aria-describedby'] as string | undefined,
      ]
        .filter(Boolean)
        .join(' ') || undefined;

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInternalValue(e.target.value);
        props?.onChange?.(e.target.value);
      },
      [props.onChange],
    );

    // Merge default and custom screen reader labels
    const srLabels = useMemo<Record<PasswordStrengthStatus, string>>(
      () => ({
        ...DEFAULT_SR_LABELS,
        ...(screenReaderStrengthLabels ?? {}),
      }),
      [screenReaderStrengthLabels],
    );

    const tooltipLabel = isVisibleState
      ? tooltipLabelHidePassword
      : tooltipLabelShowPassword;

    return (
      <div className={styles['passwordField__wrapper']}>
        <Field<AriaTextFieldProps>
          as={AriaTextField}
          className={styles.passwordField}
          label="Password"
          hasEndIcon
          {...props}
        >
          <div className={styles['passwordField']}>
            <Group>
              <Input
                ref={ref}
                type={isVisibleState ? 'text' : 'password'}
                value={value}
                onChange={handleChange}
                data-testid="alto-password-field-input"
                aria-describedby={ariaDescribedBy}
              />
              <IconButton
                size="sm"
                iconProps={
                  isVisibleState
                    ? { iconName: 'eye', iconPrefix: 'fas' }
                    : { iconName: 'eye-slash', iconPrefix: 'fas' }
                }
                onPress={() => setInternalVisible(!isVisibleState)}
                isDisabled={props?.isDisabled}
                styleVariant="neutral"
                emphasis="tertiary"
                aria-label={tooltipLabel}
                tooltipLabel={tooltipLabel}
              />
            </Group>
          </div>
        </Field>
        {props.strengthValidations?.length ? (
          <ul
            id={requirementsId}
            className={styles['passwordField__strength']}
            aria-label={requirementsLabel}
            aria-live="polite"
            aria-atomic={false}
          >
            {props.strengthValidations?.map((validation) => (
              <PasswordStrengthItem
                key={`${validation.message}-${value}`}
                status={
                  value
                    ? validation.validation(value)
                      ? 'valid'
                      : 'invalid'
                    : 'neutral'
                }
                message={validation.message}
                screenReaderStrengthLabels={srLabels}
              />
            ))}
          </ul>
        ) : null}
      </div>
    );
  },
);

/////////////////////////////////////////////
// PasswordStrengthItem Component
/////////////////////////////////////////////

const passwordStrengthItem = cva(styles.passwordStrengthItem, {
  variants: {
    status: {
      valid: styles.passwordStrengthItemValid,
      neutral: styles.passwordStrengthItemNeutral,
      invalid: styles.passwordStrengthItemInvalid,
    },
  },
  defaultVariants: {
    status: 'neutral',
  },
});

interface PasswordStrengthItemProps {
  /*
   * The status of the password strength item.
   */
  status: PasswordStrengthStatus;
  /*
   * The message to be displayed for the password strength item.
   */
  message: string;
  /*
   * Optional screen reader text for each status, used for localization
   */
  screenReaderStrengthLabels?: Partial<Record<PasswordStrengthStatus, string>>;
}

const PASSWORD_STRENGTH_MAP = {
  valid: {
    iconName: 'circle-check',
  },
  neutral: {
    iconName: 'circle-minus',
  },
  invalid: {
    iconName: 'circle-xmark',
  },
} as const satisfies Record<PasswordStrengthStatus, { iconName: string }>;

export const PasswordStrengthItem = ({
  status,
  message,
  screenReaderStrengthLabels,
}: PasswordStrengthItemProps) => {
  const { iconName } = PASSWORD_STRENGTH_MAP[status];

  return (
    <li>
      <p className={passwordStrengthItem({ status })}>
        <Icon iconName={iconName} iconPrefix="fas" />
        <span role="text">
          <span className={styles['passwordField__strength-text']} dir="auto">
            {`${screenReaderStrengthLabels?.[status]}: `}
          </span>
          <bdi dir="auto">{message}</bdi>
        </span>
      </p>
    </li>
  );
};

PasswordField.displayName = 'PasswordField';
PasswordStrengthItem.displayName = 'PasswordStrengthItem';

export default PasswordField;
